import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AtSign, BellOff, CalendarClock, CircleCheck, CreditCard, Ellipsis, Globe, History, Mail, MailOpen, Megaphone, MessageCircle, PanelRightOpen,
  RefreshCw, Settings2, Shield, UserPlus, Users, VolumeX, AlarmClock, Download,
} from 'lucide-react';
import { flattenPages } from '@missions/api-client';
import { ADMIN_ONLY_NOTIFICATION_TYPES, type Notification, type NotificationType } from '@missions/types';
import { Button, useToast } from '@missions/ui';
import { api } from '../../lib/client';
import { useApi, useErrorToast, useInfinite, useInvalidate } from '../../lib/hooks';
import { useT } from '../../lib/session';
import { Radar, rel, exact } from '../../components/atoms';
import { Menu, type MenuItem } from '../../components/Menu';

export type NotifTab = 'all' | 'unread' | 'mentions' | 'assigned';

export const NOTIF_TYPE: Record<NotificationType, { icon: ReactNode; color: string; label: string }> = {
  'mission.assigned': { icon: <UserPlus />, color: '#0457fd', label: 'Assigned to me' },
  'mission.mentioned': { icon: <AtSign />, color: '#7c3aed', label: 'Mentions' },
  'mission.commented': { icon: <MessageCircle />, color: '#0ea5e9', label: 'Comments' },
  'mission.status_changed': { icon: <RefreshCw />, color: '#e59400', label: 'Status changes' },
  'mission.due_soon': { icon: <CalendarClock />, color: '#f2751a', label: 'Due soon' },
  'mission.overdue': { icon: <AlarmClock />, color: '#e5484d', label: 'Overdue' },
  'mission.completed': { icon: <CircleCheck />, color: '#16a34a', label: 'Completed' },
  'invite.accepted': { icon: <Users />, color: '#16a34a', label: 'Invites accepted' },
  'member.joined': { icon: <Users />, color: '#16a34a', label: 'Members joined' },
  'plan.expiring': { icon: <CreditCard />, color: '#e5484d', label: 'Plan expiring' },
  'plan.changed': { icon: <CreditCard />, color: '#0457fd', label: 'Plan changes' },
  'domain.live': { icon: <Globe />, color: '#16a34a', label: 'Domain live' },
  'export.ready': { icon: <Download />, color: '#0457fd', label: 'Exports ready' },
  broadcast: { icon: <Megaphone />, color: '#7c3aed', label: 'Announcements' },
};

const stateOf = (n: Notification): 'unseen' | 'seen' | 'read' => (n.readAt ? 'read' : n.seenAt ? 'seen' : 'unseen');

/**
 * unseen → SEEN after ≥60% visible for 1s (batched POST notifications.seen) → READ on click (PLAN §8A).
 */
function useSeenObserver(root: HTMLElement | null, ids: string[]) {
  const idKey = ids.join(',');
  const qc = useQueryClient();
  const queue = useRef(new Set<string>());
  const flush = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const send = () => {
      const batch = [...queue.current];
      queue.current.clear();
      if (!batch.length) return;
      void api.call('notifications.seen', { body: { ids: batch } }).then(() => {
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[1] === 'notifications.counts' });
      });
    };
    const io = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          const id = (e.target as HTMLElement).dataset.nid!;
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            if (!timers.has(id))
              timers.set(
                id,
                setTimeout(() => {
                  timers.delete(id);
                  if (document.visibilityState !== 'visible') return;
                  (e.target as HTMLElement).classList.replace('s-unseen', 's-seen');
                  queue.current.add(id);
                  clearTimeout(flush.current);
                  flush.current = setTimeout(send, 400);
                }, 1000),
              );
          } else {
            clearTimeout(timers.get(id));
            timers.delete(id);
          }
        }),
      { root: root.closest('.nscroll') ?? null, threshold: [0, 0.6, 1] },
    );
    root.querySelectorAll<HTMLElement>('.ni.s-unseen').forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      timers.forEach((t) => clearTimeout(t));
      send();
    };
  }, [root, idKey, qc]);
}

export function NotificationItem({ n, onOpen }: { n: Notification; onOpen: (n: Notification) => void }) {
  const ty = NOTIF_TYPE[n.type];
  const st = stateOf(n);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState(false);
  const invalidate = useInvalidate();
  const toast = useToast();
  const onErr = useErrorToast();
  const t = useT();
  const nav = useNavigate();
  const adminOnly = ADMIN_ONLY_NOTIFICATION_TYPES.includes(n.type);
  const items: MenuItem[] = [
    st === 'read'
      ? { label: 'Mark as unread', icon: <Mail />, onClick: () => void api.call('notifications.unread', { params: { id: n.id } }).then(() => invalidate('notifications')).catch(onErr) }
      : { label: 'Mark as read', icon: <MailOpen />, onClick: () => void api.call('notifications.read', { params: { id: n.id } }).then(() => invalidate('notifications')).catch(onErr) },
    ...(n.entity.type === 'mission' && n.entity.id
      ? ([
          { label: `Open ${t.item}`, icon: <PanelRightOpen />, onClick: () => onOpen(n) },
          {
            label: `Mute this ${t.item}`,
            icon: <BellOff />,
            onClick: () =>
              void api
                .call('notifications.muteMission', { body: { missionId: n.entity.id!, muted: true } })
                .then(() =>
                  toast(`You won’t get notifications for this ${t.item}`, {
                    type: 'info',
                    action: { label: 'Undo', onClick: () => void api.call('notifications.muteMission', { body: { missionId: n.entity.id!, muted: false } }) },
                  }),
                )
                .catch(onErr),
          },
        ] satisfies MenuItem[])
      : []),
    {
      label: `Turn off “${ty.label}”`,
      icon: <VolumeX />,
      onClick: () =>
        void api
          .call('notifications.preferences')
          .then((p) => api.call('notifications.updatePreferences', { body: { muteTypes: Array.from(new Set([...p.muteTypes, n.type])) } }))
          .then(() => toast(`“${ty.label}” notifications turned off`, { type: 'info' }))
          .catch(onErr),
    },
    '-',
    { label: 'Notification preferences', icon: <Settings2 />, onClick: () => nav('/settings/notifications') },
  ];
  return (
    <div
      className={`ni s-${st}`}
      data-nid={n.id}
      role="button"
      tabIndex={0}
      aria-label={`${st === 'unseen' ? 'New. ' : st === 'seen' ? 'Unread. ' : ''}${n.title}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onOpen(n);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(n);
      }}
    >
      <div className="ni-left">
        {n.actor ? (
          <>
            <span className="ni-av" style={{ background: n.actor.color }}>{n.actor.initials}</span>
            <span className="ni-type" style={{ background: ty.color }}>{ty.icon}</span>
          </>
        ) : (
          <span className="ni-av sys" style={{ background: `color-mix(in srgb,${ty.color} 16%,var(--surface))`, color: ty.color }}>{ty.icon}</span>
        )}
      </div>
      <div className="ni-body">
        <div className="ni-text">{n.title}</div>
        {n.body && <div className="ni-quote">{n.body}</div>}
        <div className="ni-meta">
          {n.project && (
            <span className="ni-proj">
              <span className="dot" style={{ background: n.project.color }} />
              <span>{n.project.name}</span>
            </span>
          )}
          {adminOnly && <span className="ni-role"><Shield />Admins only</span>}
          <span data-tooltip={exact(n.createdAt)}>{rel(n.createdAt)}</span>
        </div>
      </div>
      <div className="ni-right">
        <span className="ni-dot" aria-hidden="true" />
        <button ref={moreRef} className="ni-more" onClick={() => setMenu((o) => !o)} aria-label="Notification options">
          <Ellipsis />
        </button>
        <Menu anchorRef={moreRef} open={menu} onClose={() => setMenu(false)} title="Notification" items={items} alignRight />
      </div>
    </div>
  );
}

/** Tabs + grouped list (Today / Yesterday / Earlier) + "Load older" with cursor pagination. */
export function NotificationFeed({ tab, onOpened, limit, compact }: { tab: NotifTab; onOpened?: () => void; limit?: number; compact?: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const onErr = useErrorToast();
  const q = useInfinite('notifications.list', { query: { tab, limit: limit ?? 20 } });
  const items = flattenPages(q.data);
  const shown = compact ? items.slice(0, limit ?? 4) : items;
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  useSeenObserver(host, shown.filter((n) => !n.seenAt).map((n) => n.id));

  const read = useMutation({
    mutationFn: (id: string) => api.call('notifications.read', { params: { id } }),
    onSettled: () => qc.invalidateQueries({ predicate: (x) => typeof x.queryKey[1] === 'string' && x.queryKey[1].startsWith('notifications.') }),
  });
  const open = (n: Notification) => {
    if (!n.readAt) read.mutate(n.id, { onError: onErr });
    onOpened?.();
    if (n.href) nav(n.href.replace(/^\/p\/([^/]+)\/m\/([^/?#]+)$/, '/p/$1?m=$2'));
  };

  if (q.isPending)
    return (
      <div className="nlist">
        {Array.from({ length: compact ? 3 : 5 }, (_, i) => (
          <div className="ni" key={i}>
            <span className="ui-skel" style={{ width: 38, height: 38, borderRadius: '50%', flex: 'none' }} />
            <div style={{ flex: 1 }}>
              <span className="sk ui-skel" style={{ width: '85%', display: 'block', height: 13 }} />
              <span className="sk ui-skel" style={{ width: '40%', display: 'block', height: 11, marginTop: 10 }} />
            </div>
          </div>
        ))}
      </div>
    );
  if (q.isError)
    return (
      <div className="empty" style={{ padding: '32px 20px' }}>
        <h3>Couldn’t load notifications</h3>
        <div className="acts"><Button onClick={() => void q.refetch()}><RefreshCw />Try again</Button></div>
      </div>
    );
  if (!shown.length)
    return (
      <div className="empty" style={{ padding: '40px 20px' }}>
        <Radar size={80} style={{ margin: '0 auto 16px' }} />
        <h3>{tab === 'all' || tab === 'unread' ? 'All clear. Nothing needs you right now.' : 'Nothing here yet'}</h3>
        <p>
          {tab === 'mentions'
            ? 'When someone @mentions you, it’ll show up here.'
            : tab === 'assigned'
              ? 'Missions assigned to you will show up here.'
              : 'New notifications about your missions will show up here.'}
        </p>
      </div>
    );

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yest = today.getTime() - 864e5;
  let g: string | null = null;
  const rows: ReactNode[] = [];
  for (const n of shown) {
    const t = new Date(n.createdAt).getTime();
    const grp = t >= today.getTime() ? 'Today' : t >= yest ? 'Yesterday' : 'Earlier';
    if (!compact && grp !== g) {
      g = grp;
      rows.push(<div className="ngroup-h" key={`g-${grp}`}>{grp}</div>);
    }
    rows.push(<NotificationItem key={n.id} n={n} onOpen={open} />);
  }
  return (
    <div className="nlist" ref={setHost}>
      {rows}
      {!compact &&
        (q.hasNextPage ? (
          <div className="n-more">
            <Button variant="ghost" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
              <History />Load older
            </Button>
          </div>
        ) : (
          <div className="n-end">That’s everything from the last 30 days</div>
        ))}
    </div>
  );
}

export function NotifTabs({ tab, onTab }: { tab: NotifTab; onTab: (t: NotifTab) => void }) {
  const c = useCountsLite();
  const tabs: [NotifTab, string, number | undefined][] = [
    ['all', 'All', undefined],
    ['unread', 'Unread', c?.unread],
    ['mentions', 'Mentions', c?.mentions],
    ['assigned', 'Assigned', c?.assigned],
  ];
  return (
    <div className="ntabs" role="tablist">
      {tabs.map(([k, l, n]) => (
        <button key={k} className={`ntab${tab === k ? ' on' : ''}`} role="tab" aria-selected={tab === k} onClick={() => onTab(k)}>
          {l}
          {n ? <span className="c">{n}</span> : null}
        </button>
      ))}
    </div>
  );
}

function useCountsLite() {
  return useApi('notifications.counts').data;
}

/** Mark-all-read with Undo (notifications.readAll → undoToken). */
export function useMarkAllRead() {
  const invalidate = useInvalidate();
  const toast = useToast();
  const onErr = useErrorToast();
  return async () => {
    try {
      const r = await api.call('notifications.readAll');
      await invalidate('notifications');
      if (!r.updated) {
        toast('Everything is already read', { type: 'info' });
        return;
      }
      toast(`Marked ${r.updated} notification${r.updated > 1 ? 's' : ''} as read`, {
        action: {
          label: 'Undo',
          onClick: () => void api.call('notifications.undoReadAll', { body: { undoToken: r.undoToken } }).then(() => invalidate('notifications')),
        },
      });
    } catch (e) {
      onErr(e);
    }
  };
}
